import { BlockControls } from "@wordpress/block-editor";
import { ToolbarButton, ToolbarGroup } from "@wordpress/components";
import { useCallback, useEffect, useRef, useState } from "@wordpress/element";
import { __, sprintf } from "@wordpress/i18n";

import {
  navHostClass,
  SliderDots,
} from "../../utils/slider-controls-skeleton";
import { controlColorVars } from "../../utils/slider-control-colors";

/**
 * Playable slider preview for the canvas, shared by the loop template blocks
 * (brandy/product-template, brandy/post-template).
 *
 * Embla never runs in the editor — it would fight the block editor for the
 * container transform — so the preview is a natively scrollable strip instead,
 * the same approach the Slider block's editor preview uses. Slide widths, gap,
 * arrow and dot skins all come from embla.min.css, which Initialize.php loads
 * admin-side, so what the canvas shows is the frontend's own control styling
 * rather than a lookalike.
 *
 * "Playing" steps the strip one slide per tick at the block's own autoplay delay,
 * which is the point: it is the only way to judge a delay without publishing.
 *
 * The strip's own styles live in _shared/_loop-slider-preview.scss, imported by
 * each block's editor.scss.
 */

const PREV_ICON = (
  <svg
    className="embla__button__svg"
    width="7"
    height="12"
    viewBox="0 0 7 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 11L1 6L6 1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const NEXT_ICON = (
  <svg
    className="embla__button__svg"
    width="7"
    height="12"
    viewBox="0 0 7 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1L6 6L1 11"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// One "step" is a single slide, matching Embla's align:start behaviour on the
// frontend rather than paging by a whole viewport.
const stepWidth = (strip, slidesPerView) =>
  strip ? strip.clientWidth / Math.max(1, slidesPerView) : 0;

export default function SliderPreview({
  blockProps,
  slider = {},
  slidesPerView = 4,
  slideCount = 0,
  children,
}) {
  const stripRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState(0);

  const showButtons = slider.showButtons ?? true;
  const showDots = !!slider.showDots;
  const loop = slider.loop ?? true;
  const delay = slider.autoPlay ? slider.autoPlayDelay ?? 3000 : 3000;

  // "" means inherit, which only the server can resolve (theme.json / the
  // brandy_slider_controls filter), so the canvas shows the built-in default —
  // same compromise the Slider block's preview makes.
  const placement = slider.controlsPlacement || "default";
  const isGrouped = placement.startsWith("grouped-");
  const navStyle = slider.controlsNav || "default";
  const dotsStyle = slider.controlsDots || "default";

  const steps = Math.max(1, slideCount - slidesPerView + 1);

  const scrollToStep = useCallback(
    (step) => {
      const strip = stripRef.current;
      if (!strip) {
        return;
      }
      const next = Math.min(Math.max(step, 0), steps - 1);
      strip.scrollTo({ left: next * stepWidth(strip, slidesPerView), behavior: "smooth" });
      setPosition(next);
    },
    [slidesPerView, steps]
  );

  const advance = useCallback(() => {
    setPosition((current) => {
      const strip = stripRef.current;
      const atEnd = current >= steps - 1;
      if (atEnd && !loop) {
        setIsPlaying(false);
        return current;
      }
      const next = atEnd ? 0 : current + 1;
      strip?.scrollTo({ left: next * stepWidth(strip, slidesPerView), behavior: "smooth" });
      return next;
    });
  }, [loop, slidesPerView, steps]);

  useEffect(() => {
    if (!isPlaying) {
      return undefined;
    }
    const timer = setInterval(advance, Math.max(500, delay));
    return () => clearInterval(timer);
  }, [isPlaying, advance, delay]);

  // A query change (fewer products, more slides per view) can leave the strip
  // parked past the last step; pull it back so the dots keep matching reality.
  useEffect(() => {
    if (position > steps - 1) {
      scrollToStep(steps - 1);
    }
  }, [steps, position, scrollToStep]);

  const prevButton = showButtons && (
    <button
      type="button"
      className="embla__button embla__button--prev"
      onClick={() => scrollToStep(position - 1)}
      aria-label={__("Previous", "brandy-blocks")}
    >
      {PREV_ICON}
    </button>
  );

  const nextButton = showButtons && (
    <button
      type="button"
      className="embla__button embla__button--next"
      onClick={() => scrollToStep(position + 1)}
      aria-label={__("Next", "brandy-blocks")}
    >
      {NEXT_ICON}
    </button>
  );

  /*
   * Shape comes from SliderDots so a dots style is previewed the same way the
   * Slider block previews it — nothing keys off `data-dots-style` in CSS, so a
   * hand-rolled `.embla__dots` here would render bullets whatever the author
   * picked. The bullets stay this block's own: unlike the inert skeleton, this
   * strip really scrolls, so a bullet is a control rather than a picture.
   */
  const dots = showDots && (
    <SliderDots
      count={steps}
      style={dotsStyle}
      position={position}
      renderBullet={(index, isSelected) => (
        <button
          type="button"
          className={`embla__dot${isSelected ? " embla__dot--selected" : ""}`}
          onClick={() => scrollToStep(index)}
          aria-label={sprintf(
            /* translators: %d: slide number. */
            __("Go to slide %d", "brandy-blocks"),
            index + 1
          )}
        />
      )}
    />
  );

  return (
    <>
      {/* Toolbar placement follows the Scrolling (marquee) block: previewing
          motion is a block-level mode, not a setting, so it belongs next to the
          block's other mode switches rather than in the inspector or as chrome
          in the canvas. */}
      <BlockControls>
        <ToolbarGroup>
          <ToolbarButton
            icon={isPlaying ? "controls-pause" : "controls-play"}
            label={
              isPlaying
                ? __("Pause slider preview", "brandy-blocks")
                : sprintf(
                    /* translators: 1: current slide number, 2: total slides. */
                    __("Play slider preview (slide %1$d of %2$d)", "brandy-blocks"),
                    position + 1,
                    steps
                  )
            }
            onClick={() => setIsPlaying((playing) => !playing)}
            isPressed={isPlaying}
          />
        </ToolbarGroup>
      </BlockControls>
      {/* `is-editor-preview` + `data-controls-placement` are the contract
          embla.min.css documents for canvas previews: with no `.embla` element
          in the editor, those two are what give the control skeleton its tokens
          and its positioning context.

          The nav host class is separate from `data-nav-style` and not redundant
          with it. The data attribute is what the frontend registry reads to pick
          a renderer; no stylesheet keys off it. `blank` is a nav style that
          emits the default buttons and skins them from an ancestor class, so
          without navHostClass() the canvas shows default arrows for it. */}
      <div
        {...blockProps}
        className={[blockProps.className, "is-editor-preview", navHostClass(navStyle)]
          .filter(Boolean)
          .join(" ")}
        // Nav colours belong on this element rather than the caller's wrapper
        // style: `is-editor-preview` is where embla.min.css seeds those
        // properties, and an element's own declaration beats an inherited one.
        style={{ ...blockProps.style, ...controlColorVars(slider) }}
        data-controls-placement={placement}
        data-nav-style={navStyle}
        data-dots-style={dotsStyle}
        data-controls-reveal={slider.controlsReveal || "always"}
      >
        <ul
          className="brandy-loop-slider-preview__slides"
          ref={stripRef}
          onScroll={() => {
            const strip = stripRef.current;
            const step = stepWidth(strip, slidesPerView);
            if (step > 0) {
              setPosition(Math.round(strip.scrollLeft / step));
            }
          }}
        >
          {children}
        </ul>
        {/* Grouping mirrors Helpers/SliderControlsMarkup::render() — grouped
            placements put the arrows either side of the dots in one row. */}
        {isGrouped ? (
          <div className="embla__controls">
            {prevButton}
            {dots}
            {nextButton}
          </div>
        ) : (
          <>
            {showButtons && (
              <div className="embla__buttons">
                {prevButton}
                {nextButton}
              </div>
            )}
            {dots}
          </>
        )}
      </div>
    </>
  );
}
